var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { uniqueId } from '../../util/id';
import Selector from '../Selector';
import { hideVisually, typography } from '../../styles/style-mixins';
import { AccessibilityError } from '../../util/errors';
const stretchStyles = ({ stretch = false }) => {
    if (stretch) {
        return css `
      display: flex;
      align-items: stretch;
      width: 100%;
    `;
    }
    return css `
    display: inline-flex;
    align-items: flex-start;
    width: auto;
  `;
};
const baseStyles = ({ theme }) => css `
  display: flex;
  flex-direction: row;
  width: 100%;

  > div:not(:last-child) {
    margin-right: ${theme.spacings.mega};
  }
`;
const Fieldset = styled.fieldset(baseStyles, stretchStyles);
const legendStyles = ({ theme }) => css `
  margin-bottom: ${theme.spacings.bit};
`;
const legendHiddenStyles = ({ hideLabel }) => hideLabel && hideVisually();
const Legend = styled('legend')(typography('two'), legendStyles, legendHiddenStyles);
const OptionItem = styled.div `
  flex: 1;
  align-self: stretch;
  & label {
    height: 100%;
  }
`;
/**
 * A group of Selectors.
 */
export const SelectorGroup = forwardRef((_a, ref) => {
    var { options, onChange, value: activeValue, name: customName, label, multiple, size, stretch, hideLabel } = _a, rest = __rest(_a, ["options", "onChange", "value", "name", "label", "multiple", "size", "stretch", "hideLabel"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('SelectorGroup', 'The `label` prop is required. Pass `hideLabel` if you intend to hide the label visually.');
    }
    const name = customName || uniqueId('selector-group_');
    if (!options) {
        return null;
    }
    return (_jsxs(Fieldset, Object.assign({ ref: ref, stretch: stretch }, rest, { children: [_jsx(Legend, Object.assign({ hideLabel: hideLabel }, { children: label })), options.map((_a) => {
                var { children, value } = _a, optionRest = __rest(_a, ["children", "value"]);
                return (_jsx(OptionItem, { children: _jsx(Selector, Object.assign({ name: name, onChange: onChange, multiple: multiple, value: value, size: size, css: css `
                width: 100%;
              `, checked: multiple ? activeValue.includes(value) : value === activeValue }, optionRest, { children: children })) }, value));
            })] })));
});
SelectorGroup.displayName = 'SelectorGroup';
